import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  cartonTypeService,
  CartonTypeFingerprintDuplicateError,
  CartonTypeAlreadyInScopeError,
  CartonTypeHasActiveReferencesError,
  CartonTypeNotFoundError,
  type CartonTypeCreateRequest,
  type CartonTypeListParams,
  type CartonTypeListResponse,
  type CartonTypeReferenceCountResponse,
  type CartonTypeUpdateRequest,
} from '../../services/api/cartonTypeService';

/**
 * Load the carton-type catalog with optional filters + paging. Used by the
 * Carton Type Catalog Admin page on mount and whenever filter / page state
 * changes (Spec A Req 1, 6).
 */
export const loadCartonTypeCatalog = createAsyncThunk<CartonTypeListResponse, CartonTypeListParams>(
  'cartonTypeCatalog/load',
  async (params, { rejectWithValue }) => {
    try {
      return await cartonTypeService.list(params);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load carton type catalog');
    }
  }
);

/**
 * Create a new carton type. Surfaces `CartonTypeFingerprintDuplicateError` to
 * the caller so the form can render a field-level error on the Fingerprint
 * input (Spec A Req 2.4).
 */
export const createCartonType = createAsyncThunk<
  { id: number; fingerprint: string },
  { tenantId: number; request: CartonTypeCreateRequest }
>(
  'cartonTypeCatalog/create',
  async ({ tenantId, request }, { rejectWithValue }) => {
    try {
      return await cartonTypeService.create(tenantId, request);
    } catch (error: unknown) {
      // Re-throw typed errors so the caller can branch on `reasonCode`.
      if (error instanceof CartonTypeFingerprintDuplicateError) {
        return rejectWithValue({ reasonCode: error.reasonCode, message: error.message });
      }
      // edi-carton-type-scoped-physical-attributes — scoped create where the
      // carton is already in this (buyer, supplier) pair's catalog.
      if (error instanceof CartonTypeAlreadyInScopeError) {
        return rejectWithValue({ reasonCode: error.reasonCode, message: error.message });
      }
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create carton type');
    }
  }
);

/**
 * Update mutable fields of a carton type. Fingerprint is immutable (P1) — any
 * value supplied is ignored server-side.
 */
export const updateCartonType = createAsyncThunk<
  { id: number },
  { tenantId: number; id: number; request: CartonTypeUpdateRequest }
>(
  'cartonTypeCatalog/update',
  async ({ tenantId, id, request }, { rejectWithValue }) => {
    try {
      return await cartonTypeService.update(tenantId, id, request);
    } catch (error: unknown) {
      if (error instanceof CartonTypeNotFoundError) {
        return rejectWithValue({ reasonCode: error.reasonCode, message: error.message });
      }
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update carton type');
    }
  }
);

/**
 * Soft-deactivate a carton type. Surfaces `CartonTypeHasActiveReferencesError`
 * with the active-reference count so the caller can render the hard-block
 * dialog (Spec A Req 4.2).
 */
export const deactivateCartonType = createAsyncThunk<
  { id: number },
  { tenantId: number; id: number }
>(
  'cartonTypeCatalog/deactivate',
  async ({ tenantId, id }, { rejectWithValue }) => {
    try {
      return await cartonTypeService.deactivate(tenantId, id);
    } catch (error: unknown) {
      if (error instanceof CartonTypeHasActiveReferencesError) {
        return rejectWithValue({
          reasonCode: error.reasonCode,
          message: error.message,
          activeReferenceCount: error.activeReferenceCount,
        });
      }
      if (error instanceof CartonTypeNotFoundError) {
        return rejectWithValue({ reasonCode: error.reasonCode, message: error.message });
      }
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to deactivate carton type');
    }
  }
);

/**
 * Reactivate a soft-deactivated carton type. Idempotent — already-active rows
 * succeed without a DB write (Spec A Req 4.5).
 */
export const reactivateCartonType = createAsyncThunk<
  { id: number },
  { tenantId: number; id: number }
>(
  'cartonTypeCatalog/reactivate',
  async ({ tenantId, id }, { rejectWithValue }) => {
    try {
      return await cartonTypeService.reactivate(tenantId, id);
    } catch (error: unknown) {
      if (error instanceof CartonTypeNotFoundError) {
        return rejectWithValue({ reasonCode: error.reasonCode, message: error.message });
      }
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to reactivate carton type');
    }
  }
);

/**
 * Load the active-reference count + first-N profile ids for a carton type.
 * Used as the pre-deactivate gate (Spec A Req 4.2) and the detail-drawer
 * "Referenced by" section (Req 5.2). The slice caches the result keyed by
 * carton id.
 */
export const loadCartonTypeReferences = createAsyncThunk<
  CartonTypeReferenceCountResponse,
  { tenantId: number; id: number; limit?: number }
>(
  'cartonTypeCatalog/loadReferences',
  async ({ tenantId, id, limit }, { rejectWithValue }) => {
    try {
      return await cartonTypeService.getReferences(tenantId, id, limit);
    } catch (error: unknown) {
      if (error instanceof CartonTypeNotFoundError) {
        return rejectWithValue({ reasonCode: error.reasonCode, message: error.message });
      }
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load carton type references');
    }
  }
);
